import React, { createContext, useContext, useState, useEffect, useCallback, FC, ReactNode } from 'react';
import axios from 'axios';
import { API_ENDPOINT } from '../apiEndpoints';
import { useAuth } from './AuthContext';

type LikedTrackIds = Set<number>;

interface LikesContextType {
  likedTrackIds: LikedTrackIds | null;
  isLiked: (trackId: number) => boolean;
  setLiked: (trackId: number, liked: boolean) => void;
  refetch: () => Promise<void>;
}

const LikesContext = createContext<LikesContextType | undefined>(undefined);

export const useLikes = (): LikesContextType | undefined => useContext(LikesContext);

export const LikesProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, token } = useAuth();
  const [likedTrackIds, setLikedTrackIds] = useState<LikedTrackIds | null>(null);

  const refetch = useCallback(async () => {
    if (!isAuthenticated) {
      setLikedTrackIds(new Set());
      return;
    }
    try {
      const response = await axios.get(`${API_ENDPOINT}/likes`, {
        headers: {
          Authorization: `Bearer ${token ?? localStorage.getItem('token') ?? ''}`
        }
      });
      const likes = response.data;
      const ids = new Set<number>();
      if (Array.isArray(likes)) {
        likes.forEach((like: { trackids: string }) => {
          try {
            const trackIds = JSON.parse(like.trackids);
            if (Array.isArray(trackIds)) {
              trackIds.forEach((id: number) => ids.add(id));
            }
          } catch {
            // ignore malformed entry
          }
        });
      }
      setLikedTrackIds(ids);
    } catch (error) {
      console.error('Error fetching likes:', error);
      setLikedTrackIds(new Set());
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const isLiked = useCallback((trackId: number) => {
    return likedTrackIds !== null && likedTrackIds.has(trackId);
  }, [likedTrackIds]);

  const setLiked = useCallback((trackId: number, liked: boolean) => {
    setLikedTrackIds((prev) => {
      if (prev === null) return prev;
      const next = new Set(prev);
      if (liked) {
        next.add(trackId);
      } else {
        next.delete(trackId);
      }
      return next;
    });
  }, []);

  const value: LikesContextType = {
    likedTrackIds,
    isLiked,
    setLiked,
    refetch,
  };

  return (
    <LikesContext.Provider value={value}>
      {children}
    </LikesContext.Provider>
  );
};
